import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelfieCapture } from "@/components/SelfieCapture";
import { SelfieImage } from "@/components/SelfieImage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { formatDuration, formatTime, formatDateTime, STATUS_META } from "@/lib/format";
import { AppNav } from "@/components/AppNav";
import {
  Fingerprint, LogOut, MapPin, Clock, LogIn, Coffee, Play, Loader2, CalendarDays, Camera,
} from "lucide-react";

function Clocky() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-right">
      <div className="font-mono text-2xl font-semibold text-slate-900" data-testid="live-clock">
        {now.toLocaleTimeString("en-GB")}
      </div>
      <div className="text-xs text-slate-500">
        {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </div>
    </div>
  );
}

function getCoords() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 6000, maximumAge: 60000 }
    );
  });
}

export default function StaffPortal() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [records, setRecords] = useState([]);
  const [selfie, setSelfie] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [liveWorked, setLiveWorked] = useState(0);
  const tickRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([api.get("/attendance/status"), api.get("/attendance/my")]);
      setStatus(s.data);
      setLiveWorked(s.data.worked_seconds);
      setRecords(m.data.records);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // live tick when working
  useEffect(() => {
    clearInterval(tickRef.current);
    if (status?.status === "working") {
      tickRef.current = setInterval(() => setLiveWorked((s) => s + 1), 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [status?.status]);

  const doAction = async (type) => {
    if ((type === "check_in" || type === "check_out") && !selfie) {
      toast.error("Please capture a selfie first");
      return;
    }
    setBusy(true);
    try {
      const coords = type === "check_in" || type === "check_out" ? await getCoords() : null;
      await api.post("/attendance/check", {
        type,
        selfie: type === "check_in" || type === "check_out" ? selfie : null,
        note,
        work_zone: "Jakarta Home Base - Zone 4",
        ...(coords || {}),
      });
      const labels = { check_in: "Checked in", check_out: "Checked out", break_start: "Break started", break_end: "Back to work" };
      toast.success(labels[type]);
      setSelfie(null);
      setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const st = STATUS_META[status?.status] || STATUS_META.checked_out;
  const isWorking = status?.status === "working";
  const isBreak = status?.status === "break";
  const isOut = !status || status?.status === "checked_out";

  return (
    <div className="min-h-screen bg-slate-50">
      <AppNav />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6 items-start">
        {/* Left: status + timer */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="overflow-hidden">
            <div className="bg-[#1F1B16] p-5 text-white">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-slate-400">Current status</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`} data-testid="current-status-badge">
                  <span className="relative flex h-2 w-2">
                    {(isWorking || isBreak) && <span className={`absolute inline-flex h-full w-full rounded-full ${st.dot} opacity-75 animate-ping-slow`} />}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${st.dot}`} />
                  </span>
                  {st.label}
                </span>
              </div>
              <div className="mt-4">
                <div className="text-xs text-slate-400 mb-1">Worked today</div>
                <div className="font-mono text-4xl font-bold tracking-tight" data-testid="worked-timer">{formatDuration(liveWorked)}</div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                <Clock className="h-3.5 w-3.5" />
                Check-in: {formatTime(status?.check_in_time)}
              </div>
            </div>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <MapPin className="h-4 w-4 text-indigo-500" />
                Jakarta Home Base - Zone 4
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                Device auto-detected · WIB (UTC+7)
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Break controls</span>
                <Coffee className="h-4 w-4 text-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" disabled={!isWorking || busy} onClick={() => doAction("break_start")} data-testid="break-start-btn">
                  <Coffee className="h-4 w-4" /> Break
                </Button>
                <Button variant="outline" disabled={!isBreak || busy} onClick={() => doAction("break_end")} data-testid="break-end-btn">
                  <Play className="h-4 w-4" /> Resume
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Middle: selfie + action */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Camera className="h-5 w-5 text-indigo-600" />
                <h3 className="font-display font-semibold text-lg text-slate-900">
                  {isOut ? "Check in for the day" : "Check out"}
                </h3>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <SelfieCapture onCapture={setSelfie} capturedImage={selfie} onClear={() => setSelfie(null)} />
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Work note (optional)</label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="What are you working on today?"
                      className="mt-1.5 resize-none h-28"
                      data-testid="work-note-input"
                    />
                  </div>
                  {isOut ? (
                    <Button className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => doAction("check_in")} data-testid="check-in-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" /> Check in now</>}
                    </Button>
                  ) : (
                    <Button className="w-full h-12 text-base" variant="destructive" disabled={busy} onClick={() => doAction("check_out")} data-testid="check-out-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4" /> Check out</>}
                    </Button>
                  )}
                  <p className="text-xs text-slate-400 text-center">A selfie is required to verify your {isOut ? "check-in" : "check-out"}. GPS location is attached automatically when allowed by your browser.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* history */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="h-5 w-5 text-slate-500" />
                <h3 className="font-display font-semibold text-lg text-slate-900">My attendance history</h3>
              </div>
              {records.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">No records yet. Your check-ins will appear here.</p>
              ) : (
                <div className="space-y-2" data-testid="history-list">
                  {records.map((r) => (
                    <motion.div key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 border border-slate-100">
                      {r.selfie_path ? (
                        <SelfieImage path={r.selfie_path} className="h-11 w-11 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          {r.type.includes("break") ? <Coffee className="h-4 w-4 text-amber-500" /> : <Clock className="h-4 w-4 text-slate-400" />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 capitalize">{r.type.replace("_", " ")}</div>
                        {r.note && <div className="text-xs text-slate-400 truncate">{r.note}</div>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-mono text-slate-700">{formatDateTime(r.ts)}</div>
                        {r.late && r.type === "check_in" && <span className="text-xs text-rose-500 font-medium">Late</span>}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
